import type { Film, Showtime } from '@vista-digital/ocapi-types/v1'
import type { OccI18N } from '@vista-digital/occ-ui'
import { getRoutedUrl } from '@/common/utils/urls/getRoutedUrl'
import { isRouted } from '@/common/utils/urls/isRouted'
import { urlFriendly } from '@/common/utils/urls/urlFriendly'
import { navigationService } from '@/content/services/navigation.service'
import { ticketingWorkflow } from '@/content/services/workflows/ticketing/ticketing-workflow'
import type { UrlServiceInterface } from './url.service.interface'

export class UrlService implements UrlServiceInterface {
    private i18n: OccI18N
    public setI18n(i18n: OccI18N) {
        this.i18n = i18n
    }

    private versions: { [key: string]: string } = {}
    public setVersions(versions: { [key: string]: string }) {
        this.versions = versions
    }
    public getVersion(lang: string) {
        return this.versions[lang]
    }

    public getFilmDetailsUrl(
        film: Film,
        siteId: string | undefined = undefined,
        extraParams: { [key: string]: string } = {}
    ): string {
        const filmName = this.i18n?.translatable(film.title) || film.title?.text
        const filmParams = {
            filmName: this.urlFriendly(filmName),
            filmId: film.id,
        }
        const params = {
            ...extraParams,
            ...(siteId ? { ...filmParams, cinema: siteId } : filmParams),
        }

        return navigationService.getPageUrl('FilmDetails', params)
    }

    public getFilmDetailsUrlForVersion(filmsPageUrl: string, filmName: string, filmId: string) {
        return `${filmsPageUrl}/${this.urlFriendly(filmName)}/${filmId}/`
    }

    public getShowtimeUrl(showtime: Showtime, extraParams: { [key: string]: string } = {}): string {
        const state = ticketingWorkflow.reloadStateFromStorage()

        const destinationPage = state?.currentPage ? state.currentPage : 'SeatPicker'

        return navigationService.getPageUrl(
            'Ticketing',
            { showtimeId: showtime.id, ...extraParams },
            true,
            destinationPage as string
        )
    }

    // Takes a string and returns it in an url-compatible format
    public urlFriendly(urlInput: string): string {
        return urlFriendly(urlInput)
    }

    public isRouted(url: string) {
        return isRouted(url)
    }

    public getRoutedUrl(url: string) {
        return getRoutedUrl(url)
    }

    public lastUrlSegment(url: string) {
        return url
            .split('/')
            .filter(i => i)
            .pop()
    }

    public removeLastUrlSegment(url: string) {
        if (url.endsWith('/')) {
            url = url.substring(0, url.length - 1)
        }

        return url.substring(0, url.lastIndexOf('/'))
    }

    public getPathFromUrl(url: string) {
        if (!url || url.length < 1) return null

        if (url.startsWith('/')) {
            return url
        }

        try {
            const urlObj = new URL(url)
            return urlObj.pathname
        } catch {
            return null
        }
    }

    // Cinema
    public getCinemaUrl(cinemaName: string) {
        return navigationService.getPageUrl('Cinema', {
            cinemaName: cinemaName,
        })
    }

    // From navigationService
    public getCurrentPage = () => navigationService.getCurrentPage() as string
    public getCurrentUrl = () => navigationService.getCurrentUrl()
}

// export const urlService = new UrlService()
